import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Database } from '@/integrations/supabase/types';

type QuadroEnum = Database['public']['Enums']['quadro_enum'];
type StatusRow = Database['public']['Tables']['status_catalogo']['Row'];
type StatusInsert = Database['public']['Tables']['status_catalogo']['Insert'];
type StatusUpdate = Database['public']['Tables']['status_catalogo']['Update'];

export interface StatusCatalogo extends StatusRow {}

export function useStatusCatalogo() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: status, isLoading } = useQuery({
    queryKey: ['status-catalogo'],
    // Catálogo quase não muda, e todo card do Kanban monta este hook: sem
    // staleTime ele era refetchado a cada montagem/foco da janela. As mutações
    // abaixo já invalidam a chave quando alguém edita um status.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('status_catalogo')
        .select('*')
        .order('quadro', { ascending: true })
        .order('categoria', { ascending: true })
        .order('ordem', { ascending: true });

      if (error) throw error;
      return data as StatusCatalogo[];
    },
  });

  const createStatus = useMutation({
    mutationFn: async (newStatus: StatusInsert) => {
      const { data, error } = await supabase
        .from('status_catalogo')
        .insert(newStatus)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['status-catalogo'] });
      queryClient.invalidateQueries({ queryKey: ['colunas-quadro'] });
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
      toast({
        title: 'Status criado',
        description: 'O status foi criado com sucesso.',
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar status',
        description: error.message,
      });
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: StatusUpdate }) => {
      const { data, error } = await supabase
        .from('status_catalogo')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidar tanto o cache de status quanto o de colunas para garantir sincronização
      queryClient.invalidateQueries({ queryKey: ['status-catalogo'] });
      queryClient.invalidateQueries({ queryKey: ['colunas-quadro'] });
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
      toast({
        title: 'Status atualizado',
        description: 'O status foi atualizado com sucesso.',
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar status',
        description: error.message,
      });
    },
  });

  const deleteStatus = async (id: string) => {
    const { error } = await supabase
      .from('status_catalogo')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir status',
        description: error.message,
      });
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['status-catalogo'] });
    queryClient.invalidateQueries({ queryKey: ['colunas-quadro'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
    toast({
      title: 'Status excluído',
      description: 'O status foi excluído com sucesso.',
    });
  };

  const toggleAtivo = async (id: string, ativo: boolean) => {
    const { error } = await supabase
      .from('status_catalogo')
      .update({ ativo })
      .eq('id', id);

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar status',
        description: error.message,
      });
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['status-catalogo'] });
    queryClient.invalidateQueries({ queryKey: ['colunas-quadro'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
    toast({
      title: ativo ? 'Status ativado' : 'Status desativado',
      description: `O status foi ${ativo ? 'ativado' : 'desativado'} com sucesso.`,
    });
  };

  const toggleFinalizador = async (id: string, finalizador: boolean) => {
    const { error } = await supabase
      .from('status_catalogo')
      .update({ finalizador })
      .eq('id', id);

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar finalizador',
        description: error.message,
      });
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['status-catalogo'] });
    queryClient.invalidateQueries({ queryKey: ['colunas-quadro'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
    toast({
      title: finalizador ? 'Status marcado como finalizador' : 'Status desmarcado como finalizador',
      description: `O status foi ${finalizador ? 'marcado' : 'desmarcado'} como finalizador.`,
    });
  };

  const duplicateStatus = async (id: string) => {
    const original = status?.find((s) => s.id === id);
    if (!original) return;

    const { id: _, created_at, updated_at, ...statusData } = original;
    const newStatus = {
      ...statusData,
      nome: `${statusData.nome} (Cópia)`,
      ativo: false,
    };

    const { error } = await supabase
      .from('status_catalogo')
      .insert(newStatus);

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao duplicar status',
        description: error.message,
      });
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['status-catalogo'] });
    queryClient.invalidateQueries({ queryKey: ['colunas-quadro'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
    toast({
      title: 'Status duplicado',
      description: 'O status foi duplicado com sucesso.',
    });
  };

  return {
    status,
    isLoading,
    createStatus: createStatus.mutate,
    updateStatus: updateStatus.mutate,
    deleteStatus,
    toggleAtivo,
    toggleFinalizador,
    duplicateStatus,
  };
}
